import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Settings, Bot, Shuffle, AlertTriangle, Clock, PenLine, Magnet } from "lucide-react";
import { toast } from "sonner";
import { DriveCard } from "@/components/DriveCard";
import { CompanyCard } from "@/components/CompanyCard";
import { GreetingCard, RatingCard } from "@/components/MessageSettings";
import { HoursCard, WaitAlertCard } from "@/components/HoursSettings";

function Row({ icon: Icon, title, desc, children }) {
  return (
    <div className="flex items-start gap-4 p-5 border-b border-border">
      <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center text-emerald-700 dark:text-emerald-200 shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold">{title}</div>
        <div className="text-sm text-muted-foreground mt-0.5">{desc}</div>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [name, setName] = useState("");

  useEffect(() => { api.get("/settings").then(({ data }) => { setS(data); setName(data.assistant_name); }); }, []);

  const update = async (patch) => {
    try {
      const { data } = await api.put("/settings", patch);
      setS(data);
      toast.success("Configuração salva");
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar"); }
  };

  if (!s) return <div className="p-10 text-muted-foreground">Carregando…</div>;

  return (
    <div className="h-full overflow-auto p-6 lg:p-10">
      <div className="max-w-3xl mx-auto space-y-6">
        <h1 className="heading text-3xl font-extrabold flex items-center gap-2"><Settings className="w-7 h-7 text-emerald-600" /> Configurações</h1>
        <h2 className="heading text-lg font-bold">Empresa</h2>
        <CompanyCard />
        <h2 className="heading text-lg font-bold pt-2">Atendimento</h2>
        <Card>
          <CardContent className="p-0">
            <Row icon={Bot} title="Assistente IA responde automaticamente"
                 desc="A IA se apresenta, identifica o assunto e o setor e responde dúvidas simples. Para na hora em que um atendente assume ou responde (inclusive pelo celular).">
              <Switch checked={s.ai_auto_reply} onCheckedChange={(v) => update({ ai_auto_reply: v })} data-testid="settings-ai-toggle" />
            </Row>
            {s.ai_auto_reply && (
              <div className="px-5 py-3 border-b border-border text-xs flex items-center gap-2 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30">
                <AlertTriangle className="w-4 h-4 shrink-0" /> Ativo: a IA envia mensagens reais aos clientes pelo WhatsApp conectado.
              </div>
            )}
            <Row icon={Bot} title="Nome do assistente" desc="Como a IA se apresenta aos clientes.">
              <div className="flex gap-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} className="w-48 h-9" data-testid="settings-assistant-name" />
                <Button size="sm" variant="outline" onClick={() => update({ assistant_name: name })}>Salvar</Button>
              </div>
            </Row>
            <Row icon={Shuffle} title="Distribuição automática"
                 desc="Conversas sem responsável vão para o atendente do setor com menos atendimentos abertos.">
              <Switch checked={s.auto_assign} onCheckedChange={(v) => update({ auto_assign: v })} data-testid="settings-autoassign-toggle" />
            </Row>
            <Row icon={PenLine} title="Assinar mensagens com o nome do atendente"
                 desc="O cliente recebe o nome em negrito antes do texto, ex.: *Leonardo:* Bom dia!">
              <Switch checked={s.sign_messages} onCheckedChange={(v) => update({ sign_messages: v })} data-testid="settings-sign-toggle" />
            </Row>
            <Row icon={Clock} title="Exigir registro de ponto para usar o WhatsApp"
                 desc="Sem entrada registrada, o atendente não assume nem responde conversas. O recebimento de mensagens não é bloqueado. Exceções por atendente em Atendentes.">
              <Switch checked={s.require_timeclock} onCheckedChange={(v) => update({ require_timeclock: v })} data-testid="settings-timeclock-toggle" />
            </Row>
            <Row icon={Magnet} title="Criar lead no CRM para cada novo contato"
                 desc="Toda conversa nova recebida vira uma oportunidade na primeira coluna do funil, com retorno agendado para 2 horas depois.">
              <Switch checked={!!s.auto_lead} onCheckedChange={(v) => update({ auto_lead: v })} data-testid="settings-autolead-toggle" />
            </Row>
          </CardContent>
        </Card>
        <h2 className="heading text-lg font-bold pt-2">Mensagens automáticas</h2>
        <HoursCard s={s} update={update} />
        <GreetingCard s={s} update={update} />
        <RatingCard s={s} update={update} />
        <WaitAlertCard s={s} update={update} />
        <h2 className="heading text-lg font-bold pt-2">Integrações</h2>
        <DriveCard settings={s} onSettings={update} />
      </div>
    </div>
  );
}
